import { useState, useMemo } from 'react';
import { CheckCircle, AlertCircle, Camera, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Controller, ControllerInspectionData, ReportStyleSettings, WaterSchedule } from './types';
import { formatProgramLines } from '../../lib/waterRestrictions';

interface ReportControllerSectionProps {
  controllers: Controller[];
  controllerInspectionsData: ControllerInspectionData[];
  workCodes: Array<{ code: string; is_issue: boolean }>;
  styleSettings: ReportStyleSettings;
}

function formatWaterSchedule(schedule: WaterSchedule | null): string {
  if (!schedule || !schedule.programs || schedule.programs.length === 0) {
    return '';
  }
  const scheduledDays = new Set<string>();
  schedule.programs.forEach(program => {
    program.days.forEach(day => scheduledDays.add(day));
  });
  if (scheduledDays.size === 0) return '';
  const dayOrder = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return dayOrder.filter(day => scheduledDays.has(day)).join(', ');
}


function zoneHasIssues(
  zone: Controller['zone_inspections'][0],
  workCodesList: Array<{ code: string; is_issue: boolean }>
): boolean {
  if (zone.issues && zone.issues.length > 0) {
    return zone.issues.some(issue => {
      const wc = workCodesList.find(w => w.code === issue.work_code);
      return wc?.is_issue;
    });
  }
  return false;
}

export function ReportControllerSection({
  controllers,
  controllerInspectionsData,
  workCodes,
  styleSettings,
}: ReportControllerSectionProps) {
  const { t, i18n } = useTranslation('inspection');
  const [collapsedControllers, setCollapsedControllers] = useState<Set<string>>(new Set());
  const [issuesOnly, setIssuesOnly] = useState(false);

  const toggleCollapse = (controllerId: string) => {
    setCollapsedControllers(prev => {
      const next = new Set(prev);
      if (next.has(controllerId)) {
        next.delete(controllerId);
      } else {
        next.add(controllerId);
      }
      return next;
    });
  };

  const controllerIssueCounts = useMemo(() => {
    const counts = new Map<string, number>();
    controllers.forEach(c => {
      const issueCount = c.zone_inspections.filter(z => zoneHasIssues(z, workCodes)).length;
      counts.set(c.id, issueCount);
    });
    return counts;
  }, [controllers, workCodes]);

  return (
    <div className="report-controllers">
      <div className="report-filter-bar no-print">
        <button
          onClick={() => setIssuesOnly(prev => !prev)}
          className={`report-filter-toggle ${issuesOnly ? 'report-filter-toggle-active' : ''}`}
          style={issuesOnly ? {
            backgroundColor: styleSettings.issue_row_bg_color,
            borderColor: styleSettings.issue_status_color,
            color: styleSettings.issue_status_color,
          } : undefined}
          title={issuesOnly ? t('viewer.showAll') : t('viewer.issuesOnly')}
        >
          <Filter size={13} />
          <span>{issuesOnly ? t('viewer.showAll') : t('viewer.issuesOnly')}</span>
        </button>
      </div>

      {controllers.map((controller) => {
        const ci = controllerInspectionsData.find(c => c.controller_id === controller.id);
        const hasControllerIssues = ci && (ci.failure_codes.length > 0 || ci.notes.trim() !== '');
        const waterDays = formatWaterSchedule(controller.water_start_times);
        const scheduleLines = formatProgramLines(controller.water_start_times?.programs, i18n.language);
        const isCollapsed = collapsedControllers.has(controller.id);
        const issueCount = controllerIssueCounts.get(controller.id) || 0;

        return (
          <div key={controller.id} className="report-controller-block">
            <div
              className="report-controller-header"
              style={{ backgroundColor: styleSettings.controller_header_color }}
            >
              <div className="report-controller-header-row no-print">
                <button
                  onClick={() => toggleCollapse(controller.id)}
                  className="report-collapse-toggle"
                  title={isCollapsed ? t('viewer.expand') : t('viewer.collapse')}
                >
                  {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="report-controller-title">
                    {t('report.controllerLabel', { n: controller.controller_number })}
                    {isCollapsed && (
                      <span className="report-collapsed-summary">
                        {controller.zone_inspections.length} {t('viewer.zones')}
                        {issueCount > 0 && (
                          <span style={{ color: styleSettings.issue_status_color }}>
                            {' '}&middot; {issueCount} {issueCount === 1 ? t('viewer.issue') : t('viewer.issues')}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="report-controller-meta">
                    {controller.description}
                    {controller.location && ` • ${controller.location}`}
                  </div>
                </div>
              </div>

              <div className="report-controller-header-print">
                <div className="report-controller-title">
                  {t('report.controllerLabel', { n: controller.controller_number })}
                </div>
                <div className="report-controller-meta">
                  {controller.description}
                  {controller.location && ` • ${controller.location}`}
                </div>
              </div>

              {waterDays && (
                <div className="report-controller-schedule">
                  <span className="font-medium">{t('report.wateringDays')}</span> {waterDays}
                </div>
              )}
              {scheduleLines.length > 0 && (
                <div className="report-controller-schedule-detail">
                  {scheduleLines.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}
              {hasControllerIssues && (
                <div className="report-controller-issues">
                  <AlertCircle size={12} className="inline-block mr-1" style={{ verticalAlign: 'middle' }} />
                  <span className="font-medium">{t('report.controllerIssuesLabel')}</span>{' '}
                  {ci!.failure_codes.length > 0 && ci!.failure_codes.join(', ')}
                  {ci!.notes && ci!.notes.trim() !== '' && (
                    <span className="italic ml-1">- {ci!.notes}</span>
                  )}
                  {ci!.photo_urls.length > 0 && (
                    <span className="report-photo-ref ml-1">{t('report.seePhotoIndexParen')}</span>
                  )}
                </div>
              )}
            </div>

            <div className={`report-zone-table-wrapper ${isCollapsed ? 'report-zone-collapsed' : ''}`}>
              <table className="report-zone-table">
                <thead>
                  <tr style={{ backgroundColor: styleSettings.table_header_color }}>
                    <th style={{ color: styleSettings.table_header_label_color, width: '48px' }}>{t('report.colZone')}</th>
                    <th style={{ color: styleSettings.table_header_label_color, width: '72px' }}>{t('report.colType')}</th>
                    <th style={{ color: styleSettings.table_header_label_color, width: '64px' }}>{t('report.colStatus')}</th>
                    <th style={{ color: styleSettings.table_header_label_color }}>{t('report.colWorkCodes')}</th>
                    <th style={{ color: styleSettings.table_header_label_color }}>{t('report.colNotes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {controller.zone_inspections.map((zone) => {
                    const workCodesDisplay = zone.issues && zone.issues.length > 0
                      ? zone.issues.map(issue => issue.work_code).join(', ')
                      : zone.work_codes;

                    const notes = zone.issues && zone.issues.length > 0
                      ? zone.issues.find(issue => issue.notes && issue.notes.trim() !== '')?.notes || ''
                      : zone.notes || '';

                    const hasIssues = zoneHasIssues(zone, workCodes);

                    const uniquePhotoUrls = new Set<string>();
                    if (zone.issues && zone.issues.length > 0) {
                      zone.issues.forEach(issue => {
                        issue.photo_urls.forEach(url => uniquePhotoUrls.add(url));
                      });
                    } else if (zone.photo_urls) {
                      zone.photo_urls.forEach(url => uniquePhotoUrls.add(url));
                    }
                    const hasPhotos = uniquePhotoUrls.size > 0;

                    return (
                      <tr
                        key={zone.id}
                        className={`${hasIssues ? 'report-zone-issue-row' : ''} ${!hasIssues && issuesOnly ? 'report-zone-filtered' : ''}`}
                        style={hasIssues ? { backgroundColor: styleSettings.issue_row_bg_color } : undefined}
                      >
                        <td className="report-zone-number">{zone.zone_number}</td>
                        <td className="report-zone-type">{zone.type}</td>
                        <td>
                          {hasIssues ? (
                            <span className="report-status-issue" style={{ color: styleSettings.issue_status_color }}>
                              <AlertCircle size={11} />
                              {t('report.statusIssue')}
                            </span>
                          ) : (
                            <span className="report-status-ok" style={{ color: styleSettings.ok_status_color }}>
                              <CheckCircle size={11} />
                              {t('report.statusOk')}
                            </span>
                          )}
                        </td>
                        <td>
                          <span style={{ color: hasIssues ? styleSettings.issue_status_color : styleSettings.ok_status_color }}>
                            {workCodesDisplay}
                          </span>
                        </td>
                        <td>
                          {notes && notes.trim() !== '' && (
                            <span className="text-gray-600 italic">{/* theme-allow: printed customer report — always light */}{notes}</span>
                          )}
                          {hasPhotos && (
                            <span className="report-photo-ref no-screen">
                              <Camera size={10} className="inline-block" style={{ verticalAlign: 'middle' }} />
                              {' '}{t('report.seePhotoIndex')}
                            </span>
                          )}
                          {hasPhotos && (
                            <span className="report-photo-count no-print">
                              <Camera size={11} className="inline-block" style={{ verticalAlign: 'middle' }} />
                              {' '}{uniquePhotoUrls.size}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {issuesOnly && issueCount === 0 && (
                    <tr className="no-print">
                      <td colSpan={5} className="text-center text-gray-400 py-2 text-xs italic">{/* theme-allow: report sheet — always light */}
                        {t('viewer.noIssues')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
